'use client';

import { StockStatusIcon } from '@app/_components/StockStatusIcon';
import Link from '@components/Link';
import { useCartActions, useCartState } from '@context/cartContext';
import { Accordion, Button } from '@ngg/components';
import { getStockStatusText } from '@ngg/storefront-utils';
import Image from 'next/image';
import type { MouseEvent } from 'react';
import { useMemo, useState } from 'react';
import type { Dictionary, Variant } from 'types';

import { getMarketConfigFromLocale } from '@/config/market-configurations';
import { cn } from '@/lib/utils';
import { formatPrice } from '@/utils/priceUtils';
// import { isMadeToOrderItem } from '@/utils/product/isMadeToOrderItem';
// import { isSoonBackInStock } from '@/utils/product/isSoonBackInStock';
import { shouldShowStockIcon } from '@/utils/product/shouldShowStockIcon';

import Icon from './icon';

function LineItem({
    item,
    locale,
    dictionary,
}: {
    item: Variant;
    locale?: string;
    dictionary: Dictionary;
}) {
    const { cart } = useCartState();
    const { addItem } = useCartActions();
    const [state, setState] = useState<string>('idle');

    const inCart = useMemo(
        () => cart?.items.some((cartItem) => cartItem.partNo === item.id),
        [cart, item],
    );

    const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if (inCart || state === 'added') return;
        setState('loading');
        await addItem([
            {
                item,
                quantity: 1,
            },
        ]);
        setState('added');
    };

    // const soonBackInStock = isSoonBackInStock(
    //     item.stockStatus,
    //     item.nextDeliveryDate,
    // );
    // const isMadeToOrder = isMadeToOrderItem(
    //     item.stockStatus,
    //     item.nextDeliveryDate,
    // );
    const showStockIcon = shouldShowStockIcon(
        item.stockStatus,
        item.nextDeliveryDate,
    );

    const { contentfulLocale: contentfulLanguage } =
        getMarketConfigFromLocale(locale);
    const stockStatusText = getStockStatusText({
        locale: contentfulLanguage,
        dictionary,
        stockStatus: item.stockStatus,
        deliveryUnit: item.deliveryUnit,
        nextDeliveryDate: item.nextDeliveryDate,
        deliveryMin: item.deliveryMin?.toString(),
        deliveryMax: item.deliveryMax?.toString(),
    });

    return (
        <li key={item.id} className="relative">
            <Link href={item.ProductURL ?? ''} locale={locale} className="flex">
                {item.image && (
                    <div className="block min-h-24 w-24 flex-shrink-0 bg-grey-100">
                        <Image
                            className="h-full w-full overflow-hidden object-contain p-1 mix-blend-multiply"
                            src={item.image.src}
                            alt={item.image.alt}
                            height={100}
                            width={100}
                        />
                    </div>
                )}
                <div className="grid w-full grid-cols-12 gap-1 px-4 py-3 text-sm">
                    <span className="col-span-12 block w-full overflow-hidden truncate text-xs uppercase">
                        {item.brand}
                    </span>
                    <span className="col-span-12 block w-full overflow-hidden truncate">
                        {item.title}
                    </span>
                    <span className="col-span-12 block w-full overflow-hidden truncate">
                        {formatPrice(item.price, locale)}
                    </span>
                    <div className="col-span-12 flex w-full flex-col gap-1">
                        <div className="line-clamp-2 flex w-full items-center gap-x-1 text-xxs uppercase">
                            {showStockIcon && <StockStatusIcon />}
                            {stockStatusText}
                        </div>
                        <Button
                            onClick={handleClick}
                            isLoading={state === 'loading'}
                            className={cn(
                                'justify-self-end',
                                'h-auto self-end whitespace-nowrap px-3 py-1 text-xs',
                                !inCart && state === 'idle'
                                    ? 'btn-outlined'
                                    : 'btn-primary',
                            )}>
                            {inCart || state === 'added'
                                ? dictionary?.added
                                : dictionary?.add}
                        </Button>
                    </div>
                </div>
            </Link>
        </li>
    );
}

export default function Accessories({
    accessories,
    locale,
    dictionary,
    className,
}: {
    accessories: Variant[];
    locale?: string;
    className?: string;
    dictionary: Dictionary;
}) {
    return (
        <Accordion
            IconComponent={Icon}
            WrapperComponent="div"
            title={dictionary.recommendedAccessories}
            classNames={{
                wrapper: className,
                button: 'text-left items-center leading-4 p-4 font-medium rounded-none',
                panel: 'p-4',
            }}
            defaultOpen
            transitionProps={{ appear: false }}>
            <ul className="flex flex-col gap-2">
                {accessories.map((item) => (
                    <LineItem
                        key={item.id}
                        item={item}
                        locale={locale}
                        dictionary={dictionary}
                    />
                ))}
            </ul>
        </Accordion>
    );
}
